import React, { useEffect, useMemo, useState } from 'react';
import { api, btnDanger, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Modal, Skeleton, useToast } from '../ui/Kit';
import { fmtDateTime, PrivacyHint } from './common';

// The one-step Alex screen (bundle 4f). "Schedule Alex screen" asks only for the date + time and the candidate's time
// zone; the server fills in the job, HM, e-mail, resume, role / tech stack and the company defaults. Once scheduled:
// Reschedule and Cancel only. Also: the Technical Screen picker (candidates at "to schedule"), Unlinked interviews
// ("Link to candidate…") and "Move this Alex interview to another candidate".

export const ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Mexico_City', 'America/Sao_Paulo', 'Europe/London', 'Europe/Dublin', 'Europe/Berlin', 'Europe/Paris',
  'Europe/Amsterdam', 'Europe/Madrid', 'Europe/Warsaw', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Manila', 'Asia/Tokyo',
  'Australia/Sydney', 'UTC'];
export const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return 'UTC'; } };

// "Nov 4, 10:00 AM (America/New_York)" in a given zone
export function inZone(iso, zone) {
  if (!iso) return '—';
  try {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
    return `${d.toLocaleString(undefined, { timeZone: zone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} (${zone})`;
  } catch (e) { return fmtDateTime(iso); }
}

export function AlexScheduleDialog({ appId, onClose, onDone }) {
  const toast = useToast();
  const [pv, setPv] = useState(null);
  const [when, setWhen] = useState('');
  const [zone, setZone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/api/applications/${appId}/alex-screen`).then((d) => { setPv(d); setZone(d.timezone || browserZone()); }).catch((e) => setError(e.message));
  }, [appId]);
  const zones = useMemo(() => [...new Set([zone, browserZone(), ...ZONES].filter(Boolean))], [zone]);
  const go = async () => {
    setBusy(true); setError('');
    try {
      const r = await api(`/api/applications/${appId}/alex-screen`, { method: 'POST', body: { scheduled_datetime: when, candidate_timezone: zone } });
      toast.show(r.invite_email_sent ? 'Alex screen scheduled — invite sent' : 'Alex screen scheduled');
      onDone && onDone(r);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open title="Schedule Alex screen" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !pv || !pv.can_schedule || !when || !zone} onClick={go} data-testid="alex-schedule-confirm">{busy ? 'Scheduling…' : 'Schedule'}</button></>}>
      {!pv && !error ? <div className="space-y-2" aria-busy="true"><Skeleton className="h-4 w-2/3" /><Skeleton /></div> : pv && <>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Date and time</span>
            <input type="datetime-local" className={input} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="alex-when" /></label>
          <label className="block"><span className={labelCls}>Candidate’s time zone</span>
            <select className={input} value={zone} onChange={(e) => setZone(e.target.value)} data-testid="alex-zone">
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}</select></label>
        </div>
        <p className="text-sm text-[#334155] mt-3" data-testid="alex-summary">{pv.summary}</p>
        {!pv.can_schedule && <p className="text-sm text-[#92400E] mt-2" data-testid="alex-why-not">{pv.why_not}</p>}
      </>}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function AlexRescheduleDialog({ app, onClose, onDone }) {
  const toast = useToast();
  const ax = app.alex || {};
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true); setError('');
    try {
      const r = await api(`/api/applications/${app.id}/alex-screen/reschedule`, { method: 'POST', body: { scheduled_datetime: when } });
      toast.show('Alex screen rescheduled'); onDone && onDone(r);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open title="Reschedule Alex screen" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !when} onClick={go} data-testid="alex-reschedule-confirm">Reschedule</button></>}>
      <p className="text-sm text-[#64748B] mb-3">Now: {inZone(ax.scheduled_at, ax.timezone || 'UTC')}</p>
      <label className="block"><span className={labelCls}>New date and time ({ax.timezone || 'UTC'})</span>
        <input type="datetime-local" className={input} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="alex-new-when" /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function AlexCancelDialog({ app, onClose, onDone }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true); setError('');
    try {
      const r = await api(`/api/applications/${app.id}/alex-screen/cancel`, { method: 'POST', body: { reason: reason.trim() } });
      toast.show('Alex screen cancelled — back to “to schedule”'); onDone && onDone(r);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open title="Cancel Alex screen" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Keep it</button>
        <button className={btnDanger} disabled={busy || reason.trim().length < 3} onClick={go} data-testid="alex-cancel-confirm">Cancel the screen</button></>}>
      <p className="text-sm text-[#334155]">The candidate goes back to AI screen “to schedule”; the interview credit is returned.</p>
      <label className="block mt-3"><span className={labelCls}>Reason (recorded)</span>
        <textarea className={input} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="alex-cancel-reason" /><PrivacyHint /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

// Pick a candidate (name / e-mail search) — Link to candidate / Move
function CandidatePicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    const t = setTimeout(() => api(`/api/candidates?q=${encodeURIComponent(q.trim())}&page_size=10`).then((d) => setRows(d.rows || [])).catch(() => setRows([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <label className="block"><span className={labelCls}>Candidate</span>
        <input className={input} value={q} placeholder="Search name or e-mail" onChange={(e) => setQ(e.target.value)} data-testid="pick-candidate-search" /></label>
      <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-[#EEF1F6]" data-testid="pick-candidate-results">
        {rows.map((r) => (
          <li key={r.id}><button type="button" onClick={() => onChange(r)} aria-pressed={value && value.id === r.id}
            className={`w-full text-left px-2 py-2 text-sm ${value && value.id === r.id ? 'bg-[#E8F1FB]' : 'hover:bg-[#F5F7FB]'}`}>
            <b>{r.full_name}</b> <span className="text-xs text-[#64748B]">{r.candidate_code}{r.current_title ? ` · ${r.current_title}` : ''}</span></button></li>
        ))}
      </ul>
    </div>
  );
}

export function AlexMoveDialog({ interviewId, onClose, onDone }) {
  const toast = useToast();
  const [to, setTo] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const go = async () => {
    try {
      const r = await api(`/api/alex-screens/${interviewId}/move`, { method: 'POST', body: { candidate_id: to.id, reason: reason.trim() } });
      toast.show(`Moved to ${to.full_name}`); onDone && onDone(r);
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal open title="Move this Alex interview to another candidate" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={!to || reason.trim().length < 3} onClick={go} data-testid="alex-move-confirm">Move</button></>}>
      <p className="text-sm text-[#334155] mb-3">The interview, its result and the AI screen stage move to the candidate you pick; this person’s application goes back to before the interview. Recorded.</p>
      <CandidatePicker value={to} onChange={setTo} />
      <label className="block mt-3"><span className={labelCls}>Reason (recorded)</span>
        <textarea className={input} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="alex-move-reason" /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function AlexPickerDialog({ onClose, onPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { api('/api/alex-screens/to-schedule').then((d) => setRows(d.rows)).catch((e) => setError(e.message)); }, []);
  const shown = (rows || []).filter((r) => !q.trim() || `${r.candidate_name} ${r.job_title}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Modal open title="Schedule interview" onClose={onClose} width="max-w-lg">
      <p className="text-sm text-[#64748B] mb-2">Candidates at AI screen “to schedule” on your jobs (screen passed, submitted).</p>
      <input className={input} value={q} placeholder="Filter by name or job" onChange={(e) => setQ(e.target.value)} data-testid="alex-picker-filter" />
      {rows === null && !error ? <div className="space-y-2 mt-3" aria-busy="true"><Skeleton /><Skeleton /></div>
        : !shown.length ? <EmptyState title="Nobody to schedule" hint="Submit a candidate to AI screen from their profile first." />
          : <ul className="mt-3 divide-y divide-[#EEF1F6]" data-testid="alex-picker">
            {shown.map((r) => (
              <li key={r.application_id}><button type="button" className="w-full text-left px-2 py-2.5 hover:bg-[#F5F7FB]" onClick={() => onPick(r)}>
                <b>{r.candidate_name}</b> <span className="text-xs text-[#64748B]">{r.candidate_code}</span>
                <span className="block text-xs text-[#64748B]">{r.req_id ? `${r.req_id} · ` : ''}{r.job_title}</span></button></li>
            ))}
          </ul>}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function LinkDialog({ row, onClose, onDone }) {
  const toast = useToast();
  const [to, setTo] = useState(null);
  const [error, setError] = useState('');
  const go = async () => {
    try { await api(`/api/alex-screens/${row.interview_id}/link`, { method: 'POST', body: { candidate_id: to.id } }); toast.show(`Linked to ${to.full_name}`); onDone(); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title={`Link ${row.candidate_name}’s interview`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!to} onClick={go} data-testid="link-confirm">Link</button></>}>
      <p className="text-sm text-[#334155] mb-3">{row.why}. Pick the person this interview is really for.</p>
      <CandidatePicker value={to} onChange={setTo} />
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

// "Unlinked interviews" — the job page (jobId) and Technical Screen (all your jobs)
export function UnlinkedInterviews({ jobId, hideWhenEmpty = false }) {
  const [rows, setRows] = useState(null);
  const [link, setLink] = useState(null);
  const load = React.useCallback(() => api(`/api/alex-screens/unlinked${jobId ? `?job_id=${jobId}` : ''}`).then((d) => setRows(d.rows)).catch(() => setRows([])), [jobId]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return null;
  if (!rows.length && hideWhenEmpty) return null;
  return (
    <section className={card + ' p-5'} data-testid="unlinked-interviews">
      <h2 className="text-base font-extrabold mb-1">Unlinked interviews</h2>
      <p className="text-xs text-[#64748B] mb-3">Quali5d AI interviews whose e-mail belongs to someone with a different name — never linked automatically.</p>
      {!rows.length ? <p className="text-sm text-[#64748B]">None.</p> : (
        <ul className="divide-y divide-[#EEF1F6]">
          {rows.map((r) => (
            <li key={r.interview_id} className="py-2.5 flex flex-wrap items-center gap-2 text-sm">
              <span className="flex-1 min-w-0"><b>{r.candidate_name}</b> <span className="text-[#64748B]">{r.candidate_email}</span>
                <span className="block text-xs text-[#64748B]">{r.job_title} · {r.why}</span></span>
              {r.can_link && <button className={btnSecondary + ' text-xs py-1.5'} onClick={() => setLink(r)} data-testid="link-to-candidate">Link to candidate…</button>}
            </li>
          ))}
        </ul>
      )}
      {link && <LinkDialog row={link} onClose={() => setLink(null)} onDone={() => { setLink(null); load(); }} />}
    </section>
  );
}
